import React, { useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import styles from './Banner.module.scss'
import { BannerVariant } from '../../../../common/types/enums'

export interface BannerItem {
  id: string | number
  title: string
  description?: string
  image?: string
}

interface BannerProps {
  items: BannerItem[]
  variant?: BannerVariant
  onItemClick?: (item: BannerItem) => void
}

export const Banner: React.FC<BannerProps> = ({
  items,
  variant = BannerVariant.EXPANDED,
  onItemClick,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [loadedImages, setLoadedImages] = useState<Record<string | number, boolean>>({})

  React.useEffect(() => {
    if (!emblaApi) return

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap())
    }

    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])

  if (!items?.length) return null

  const getVariantClass = () => {
    switch (variant) {
      case BannerVariant.EXPANDED:
        return styles.expanded
      case BannerVariant.SPLIT:
        return styles.split
      case BannerVariant.COMPACT:
        return styles.compact
      case BannerVariant.HERO:
        return styles.hero
      default:
        return styles.base
    }
  }

  const handleImageLoad = (itemId: string | number) => {
    setLoadedImages(prev => ({ ...prev, [itemId]: true }))
  }

  return (
    <div className={`${styles.banner} ${getVariantClass()}`}>
      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.container}>
          {items.map((item) => {
            const isImageLoaded = loadedImages[item.id]
            
            return (
              <div key={item.id} className={styles.slide} onClick={() => onItemClick?.(item)}>
                <div className={styles.imageContainer}>
                  {item.image ? (
                    <>
                      <img 
                        className={styles.image} 
                        src={item.image} 
                        alt={item.title}
                        onLoad={() => handleImageLoad(item.id)}
                      />
                      {!isImageLoaded && <div className={styles.imagePlaceholder} />}
                    </>
                  ) : (
                    <div className={styles.placeholder}>{item.title}</div>
                  )}
                </div>

                {isImageLoaded && (
                  <div className={styles.content}>
                    <h3 className={styles.title}>{item.title}</h3>
                    {item.description && <p className={styles.description}>{item.description}</p>}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {items.length > 1 && (
        <>
          <button className={styles.arrowPrev} onClick={() => emblaApi?.scrollPrev()}>
            ‹
          </button>
          <button className={styles.arrowNext} onClick={() => emblaApi?.scrollNext()}>
            ›
          </button>

          <div className={styles.dots}>
            {items.map((item, index) => (
              <button
                key={item.id}
                className={`${styles.dot} ${index === selectedIndex ? styles.dotActive : ''}`}
                onClick={() => emblaApi?.scrollTo(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}